import { useState, useEffect, useRef, useCallback } from "react";
import type { WindowState } from "../../context/WindowManagerContext";

interface TaskbarPreviewProps {
  /** The label for the app (used as title if no window is open) */
  label: string;
  /** The matching window state, if any */
  windowState?: WindowState;
  /** Anchor element to position the preview above */
  anchorRef: React.RefObject<HTMLElement | null>;
}

export function TaskbarPreview({ label, windowState, anchorRef }: TaskbarPreviewProps) {
  const [position, setPosition] = useState({ left: 0, bottom: 0 });
  const previewRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const previewWidth = 200;
    let left = rect.left + rect.width / 2 - previewWidth / 2;
    // Clamp to viewport
    if (left < 8) left = 8;
    if (left + previewWidth > window.innerWidth - 8) left = window.innerWidth - 8 - previewWidth;
    const bottom = window.innerHeight - rect.top + 8;
    setPosition({ left, bottom });
  }, [anchorRef]);

  const status = windowState?.status;
  const title = windowState?.title ?? label;

  return (
    <div
      ref={previewRef}
      className="taskbar-preview"
      style={{ left: position.left, bottom: position.bottom }}
    >
      <div className="taskbar-preview__content">
        <div className="taskbar-preview__header">
          {status && (
            <span
              className={`taskbar-preview__dot ${
                status === "minimized"
                  ? "taskbar-preview__dot--minimized"
                  : "taskbar-preview__dot--open"
              }`}
            />
          )}
          <span className="taskbar-preview__title">{title}</span>
        </div>
        {status && (
          <span className="taskbar-preview__status">
            {status === "minimized" ? "Minimized" : status === "maximized" ? "Maximized" : "Open"}
          </span>
        )}
        {!status && (
          <span className="taskbar-preview__status">Not running</span>
        )}
      </div>
      <div className="taskbar-preview__arrow" />
    </div>
  );
}

/**
 * Hook for managing hover-delay preview state.
 * Returns { hovered, onMouseEnter, onMouseLeave } for a single item.
 */
export function usePreviewHover(delay = 300) {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onMouseEnter = useCallback(() => {
    timerRef.current = setTimeout(() => setVisible(true), delay);
  }, [delay]);

  const onMouseLeave = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setVisible(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { visible, onMouseEnter, onMouseLeave };
}
